import { Injectable, OnDestroy, TemplateRef, Type } from '@angular/core';
import {
  NzDrawerRef,
  NzDrawerService,
  NzDrawerOptions,
} from 'ng-zorro-antd/drawer';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { DomUtilsService } from './dom-utils.service';

export interface DrawerState {
  ref: NzDrawerRef;
  params$: BehaviorSubject<unknown>;
  title$: BehaviorSubject<string | TemplateRef<object>>;
}

@Injectable({
  providedIn: 'root',
})
export class DrawerStateManager implements OnDestroy {
  private readonly DEFAULT_WIDTH = 720;
  private readonly drawerStates = new Map<string, DrawerState>();
  private readonly destroy$ = new Subject<void>();
  private isBulkClosing = false;

  constructor(
    private readonly nzDrawerService: NzDrawerService,
    private readonly domUtils: DomUtilsService
  ) {}

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();

    this.drawerStates.forEach(state => {
      state.params$.complete();
      state.title$.complete();
    });
    this.drawerStates.clear();
  }

  createDrawer<T extends object, P = void>(
    component: Type<T>,
    title: TemplateRef<object> | string,
    width: number = this.DEFAULT_WIDTH,
    nzClosable = true,
    footer?: TemplateRef<object>,
    params?: P,
    drawerId: string = crypto.randomUUID()
  ): { ref: NzDrawerRef; id: string } {
    if (!component) {
      console.error('Cannot open drawer: Component is required');
      throw new Error('Component is required to open drawer');
    }

    // Check if drawer already exists
    // if (this.drawerStates.has(drawerId)) {
    //   console.warn(`Drawer with ID ${drawerId} already exists.`);
    //   return { ref: this.drawerStates.get(drawerId)!.ref, id: drawerId };
    // }

    const drawerConfig: Partial<NzDrawerOptions> = {
      nzTitle: title,
      nzContent: component,
      nzWidth: width > 0 ? width : this.DEFAULT_WIDTH,
      nzClosable,
      nzFooter: footer,
      nzOnCancel: (event?: MouseEvent) =>
        this.handleDrawerCancel(drawerId, event),
    };

    const drawerRef = this.nzDrawerService.create(drawerConfig);

    const drawerState: DrawerState = {
      ref: drawerRef,
      params$: new BehaviorSubject<unknown>(params ?? null),
      title$: new BehaviorSubject<string | TemplateRef<object>>(title),
    };

    this.drawerStates.set(drawerId, drawerState);

    drawerRef.afterClose.subscribe(() => {
      this.resetDrawerState(drawerId);
    });

    return { ref: drawerRef, id: drawerId };
  }

  getDrawerRef(drawerId: string): NzDrawerRef | null {
    return this.drawerStates.get(drawerId)?.ref || null;
  }

  getAllDrawerIds(): string[] {
    return Array.from(this.drawerStates.keys());
  }

  updateTitle(
    drawerId: string,
    newTitle: string | TemplateRef<object>
  ): boolean {
    if (!drawerId) {
      console.warn('Cannot update drawer title: drawerId is required');
      return false;
    }

    const drawerState = this.drawerStates.get(drawerId);
    if (!drawerState) {
      console.warn(`Cannot update title: Drawer with ID ${drawerId} not found`);
      return false;
    }

    try {
      const drawerIndex = Array.from(this.drawerStates.keys()).indexOf(
        drawerId
      );
      const currentDrawer = this.domUtils.findDrawerElement(drawerIndex);

      if (currentDrawer && typeof newTitle === 'string') {
        this.domUtils.updateDrawerTitle(currentDrawer, newTitle);
      }

      drawerState.title$.next(newTitle);
      return true;
    } catch (error) {
      console.error(`Error updating drawer title: ${error}`);
      return false;
    }
  }

  setParams<P>(drawerId: string, params: P): boolean {
    if (!drawerId) {
      console.warn('Cannot set params: drawerId is required');
      return false;
    }

    const drawerState = this.drawerStates.get(drawerId);
    if (!drawerState) {
      console.warn(`Cannot set params: Drawer with ID ${drawerId} not found`);
      return false;
    }

    drawerState.params$.next(params);
    return true;
  }

  getParams<P>(drawerId: string): Observable<P | null> {
    const drawerState = this.drawerStates.get(drawerId);
    if (!drawerState) {
      return new BehaviorSubject<null>(null).asObservable();
    }
    return drawerState.params$.asObservable() as Observable<P | null>;
  }

  getTitle(drawerId: string): Observable<string | TemplateRef<object>> {
    const drawerState = this.drawerStates.get(drawerId);
    if (!drawerState) {
      return new BehaviorSubject<string>('').asObservable();
    }
    return drawerState.title$.asObservable();
  }

  closeDrawer(drawerId: string): boolean {
    const drawerState = this.drawerStates.get(drawerId);
    if (drawerState) {
      drawerState.ref.close();
      return true;
    }
    return false;
  }

  closeAllDrawers(): void {
    this.isBulkClosing = true;
    this.drawerStates.forEach((state, id) => {
      this.closeDrawer(id);
    });
    this.isBulkClosing = false;
  }

  getLastDrawerId(): string | null {
    const keys = Array.from(this.drawerStates.keys());
    return keys.at(-1) ?? null;
  }

  goBack(): boolean {
    const lastDrawerId = this.getLastDrawerId();
    if (!lastDrawerId) {
      return false;
    }

    return this.closeDrawer(lastDrawerId);
  }

  findDrawerElement(drawerId: string): HTMLElement | null {
    if (!drawerId || !this.drawerStates.has(drawerId)) return null;

    try {
      const drawerIndex = Array.from(this.drawerStates.keys()).indexOf(
        drawerId
      );
      if (drawerIndex === -1) return null;

      return this.domUtils.findDrawerElement(drawerIndex);
    } catch (error) {
      console.error(`Error finding drawer element: ${error}`);
      return null;
    }
  }

  private handleDrawerCancel(
    drawerId: string,
    event?: MouseEvent
  ): Promise<boolean> {
    if (this.isBulkClosing) {
      return Promise.resolve(true);
    }

    const drawerState = this.drawerStates.get(drawerId);
    if (!drawerState) {
      return Promise.resolve(true);
    }

    const drawerParams = drawerState.params$.getValue();
    if (this.shouldShowDirtyCloseConfirmation(drawerParams)) {
      drawerState.params$.next({
        ...(drawerParams as Record<string, unknown>),
        showCloseConfirmationDialog: true,
      });
      return Promise.resolve(false);
    }

    if (this.isCloseButtonCancel(event)) {
      if (this.hasBackButton(drawerId)) {
        this.closeAllDrawers();
      } else {
        this.goBack();
      }
      return Promise.resolve(false);
    }

    this.goBack();
    return Promise.resolve(false);
  }

  private isCloseButtonCancel(event?: MouseEvent): boolean {
    const target = event?.target;
    if (target instanceof Element && target.closest('.ant-drawer-close')) {
      return true;
    }

    const activeElement = document.activeElement;
    return (
      activeElement instanceof Element &&
      activeElement.closest('.ant-drawer-close') !== null
    );
  }

  private hasBackButton(drawerId: string): boolean {
    const drawerElement = this.findDrawerElement(drawerId);
    return drawerElement?.querySelector('.back-button') !== null;
  }

  private shouldShowDirtyCloseConfirmation(params: unknown): boolean {
    if (!params || typeof params !== 'object') {
      return false;
    }

    const typedParams = params as Record<string, unknown>;
    return (
      typedParams['requireDirtyCloseConfirmation'] === true &&
      typedParams['isDirty'] === true
    );
  }

  private resetDrawerState(drawerId: string): void {
    const drawerState = this.drawerStates.get(drawerId);
    if (drawerState) {
      drawerState.params$.complete();
      drawerState.title$.complete();
      this.drawerStates.delete(drawerId);
    }
  }
}
